import { Component, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { FormControlNames } from '../../../../référentiel-métier-parcours/models/form-control-names.model';
import { ButtonAddNewComponent } from '../../../../shared/components/button-add-new/button-add-new.component';
import {
  ExpansionPanel,
  ExpansionPanelMenuAction,
} from '../../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { TrigrammeTypeCertification } from '../../models/trigramme-type-certification.model';
import { CreateRequestTrigrammeType } from '../../request/create-request';
import { TrigrammeTypeCertificationStore } from '../../store/trigrammes-type-certification.store';

@Component({
  selector: 'app-form-type-certification',
  imports: [
    ReactiveFormsModule,
    HlmFieldImports,
    ButtonAddNewComponent,
    HlmInputImports,
    HlmCardImports,
    ExpansionPanel,
    LabelRequiredDirective,
  ],
  templateUrl: './form-trigramme-type-certification.component.html',
})
export class FormTrigrammeTypeCertificationComponent implements OnInit {
  readonly store = inject(TrigrammeTypeCertificationStore);
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);

  readonly trigrammeType = input<TrigrammeTypeCertification>();

  form = this.#fb.group({
    [FormControlNames.TRIGRAMMES_TYPE]: this.#fb.array([]),
  });

  readonly trigrammeActions: ExpansionPanelMenuAction<number>[] = [
    {
      icon: 'lucideTrash',
      invisible: (idx) => idx === 0,
      label: 'Supprimer',
      class: 'invisible group-hover/item:visible',
      action: (idx) => this.trigrammesType.removeAt(idx!),
    },
  ];

  constructor() {
    effect(() => {
      const trigrammeType = this.trigrammeType();

      if (trigrammeType) {
        this.trigrammesType.clear();
        this.ajouterTrigrammeType(trigrammeType);
      }
    });
  }

  ngOnInit(): void {
    this.ajouterTrigrammeType();

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  ajouterTrigrammeType(trigrammeType?: TrigrammeTypeCertification) {
    const form = this.#fb.group({
      id: [trigrammeType?.id],
      nom: [trigrammeType?.nom, [Validators.required]],
      code: [trigrammeType?.code, [Validators.required, Validators.pattern('^[A-Z]{3}$')]],
    });
    this.trigrammesType.push(form);
  }

  onSubmit() {
    const toastrId = uuidv7();
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const trigrammeType = this.trigrammeType();

    if (trigrammeType) {
      const trigrammeModifié = (this.trigrammesType.value as TrigrammeTypeCertification[])[0];
      this.store.modifier({
        trigrammeType: trigrammeModifié,
        next: () => {
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: `Modification du trigramme ${trigrammeModifié.nom} réussie`,
              id: toastrId,
            },
            id: toastrId,
          });
          this.#sidepanelService.close();
          this.store.charger();
        },
      });
    } else {
      const data: CreateRequestTrigrammeType = {
        trigrammesTypeCertification: (this.trigrammesType.value as TrigrammeTypeCertification[]).map((technique) => ({
          nom: technique.nom,
          code: technique.code,
        })),
      };

      this.store.ajouter({
        data,
        next: () => {
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: 'Création des trigrammes réussie',
              id: toastrId,
            },
            id: toastrId,
          });
          this.#sidepanelService.close();
          this.store.charger();
        },
      });
    }
  }

  get trigrammesType() {
    return this.form.get(FormControlNames.TRIGRAMMES_TYPE) as FormArray;
  }
}
